import React, { useState, useEffect } from "react";
import axios from "axios";
import SpotifyWebApi from "spotify-web-api-node"
import Modal from "./Modal";

const CreateCampaign = ({ accessToken, pointsRef, points, campaignsRef }) => {
    const [trackUri, setTrackUri] = useState("");
    const [budgetPoints, setBudgetPoints] = useState(50);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState("");
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [pointsFor30Sec, setPointsFor30Sec] = useState(1);
    const [pointsForHalf, setPointsForHalf] = useState(2);
    const [pointsForFull, setPointsForFull] = useState(5);
    const [trackData, setTrackData] = useState(null);

    const spotifyApi = new SpotifyWebApi();
    spotifyApi.setAccessToken(accessToken);

    const extractTypeAndId = (url) => {
        const match = url.match(/open\.spotify\.com\/(track|playlist|album|show|episode)\/([a-zA-Z0-9]{22})/);
        if (!match) {
            throw new Error("Invalid Spotify URL");
        }
        const type = match[1];
        const id = match[2];
        return { type, id };
    };

    const fetchTrackData = async (url) => {
        try {
            const { type, id } = extractTypeAndId(url);
            let trackInfo;

            switch (type) {
                case "track":
                    trackInfo = await spotifyApi.getTrack(id);
                    break;
                case "playlist":
                    trackInfo = await spotifyApi.getPlaylist(id);
                    break;
                case "album":
                    trackInfo = await spotifyApi.getAlbum(id);
                    break;
                default:
                    throw new Error("Unsupported entity type");
            }

            let smallestImage = null;
            const images = type === "track" ? trackInfo.body.album?.images : trackInfo.body.images;

            if (images && images.length > 0) {
                smallestImage = images.reduce((smallest, image) => {
                    if (!smallest || image.width < smallest.width) {
                        return image;
                    }
                    return smallest;
                }, null);
            }

            setTrackData({
                name: trackInfo.body.name || trackInfo.body.title || "Unknown",
                artist: trackInfo.body.artists?.[0]?.name || trackInfo.body.owner?.display_name || "Unknown Artist",
                image: smallestImage ? smallestImage.url : "",
            });
        } catch (error) {
            setTrackData(null);
        }
    };

    useEffect(() => {
        fetchTrackData(trackUri);
    }, [trackUri]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setMessage("");

        if (!accessToken) {
            setMessage("Access token is missing");
            setLoading(false);
            return;
        }

        try {
            const { type, id } = extractTypeAndId(trackUri);
            const spotifyUri = `spotify:${type}:${id}`;

            const response = await axios.post(
                `${process.env.REACT_APP_BACK4APP_SERVER_PATH}/api/create-campaign`,
                {
                    trackUri: spotifyUri,
                    budgetPoints,
                    accessToken,
                    pointsFor30Sec,
                    pointsForHalf,
                    pointsForFull
                }
            );

            setPointsFor30Sec(1);
            setPointsForHalf(2);
            setPointsForFull(5);
            setMessage(response.data.message);
            setTrackUri("");
            setBudgetPoints(50);
            setTrackData(null);
            if (pointsRef.current) {
                pointsRef.current.refreshPoints();
            }
            if (campaignsRef.current) {
                campaignsRef.current.refreshCampaigns();
            }
        } catch (error) {
            if (error.message === "Invalid Spotify URL") {
                setMessage("Invalid Spotify URL format");
            } else {
                setMessage("Error creating campaign: " + (error.response?.data?.error || "Unknown error"));
            }
        } finally {
            setLoading(false);
        }
    };


    const handleSliderChange = (e) => {
        setBudgetPoints(e.target.value);
    };

    const sliderStyle = {
        background: `linear-gradient(90deg, #9fff9a ${((budgetPoints - 50) / (points - 50)) * 100}%, #000 ${((budgetPoints - 50) / (points - 50)) * 100}%)`,
    };

    return (
        <div>
            <button onClick={() => setIsModalOpen(true)} className="open-modal-btn">
                Create Campaign
            </button>
            <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title="Create Campaign">
                <form onSubmit={handleSubmit}>
                    <div className="form-group">
                        <input
                            type="text"
                            value={trackUri}
                            onChange={(e) => setTrackUri(e.target.value)}
                            placeholder="Insert a Spotify song, playlist, or podcast link"
                            className="input-field"
                            required
                        />
                    </div>
                    {trackData && (
                        <div className="form-group">
                            <div class="campaign-song">
                                <div class="campaign-song-img">
                                    {trackData.image && <img src={trackData.image} alt={trackData.name} width={68} />}
                                </div>
                                <div class="campaign-song-data">
                                    <div class="campaign-song-title">
                                        {trackData.name}
                                    </div>
                                    <div class="campaign-song-author">
                                        {trackData.artist}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                    <div className="form-group slider-container">
                        <label className="slider-value">{budgetPoints}</label>
                        <input
                            type="range"
                            className="slider"
                            min="50"
                            max={points}
                            step={10}
                            value={budgetPoints}
                            onChange={handleSliderChange}
                            style={sliderStyle}
                        />
                        <div className="slider-labels">
                            <span>50</span>
                            <span>{points}</span>
                        </div>
                    </div>
                    <div className="form-group points-input-group">
                        <label>Points for 30 seconds:</label>
                        <div className="input-wrapper">
                            <div class="adjust-wrapper">
                                <button
                                    className="input-button increase"
                                    onClick={() => setPointsFor30Sec((prev) => prev + 1)}
                                    type="button"
                                >

                                </button>
                                <button
                                    className="input-button decrease"
                                    onClick={() => setPointsFor30Sec((prev) => Math.max(prev - 1, 1))}
                                    type="button"
                                >

                                </button>
                            </div>
                            <input
                                type="number"
                                value={pointsFor30Sec}
                                onChange={(e) => setPointsFor30Sec(Number(e.target.value))}
                                className="points-input"
                                min="1"
                                required
                            />

                        </div>
                    </div>
                    <div className="form-group points-input-group">
                        <label>Points for half track:</label>
                        <div className="input-wrapper">
                            <div class="adjust-wrapper">
                                <button
                                    className="input-button increase"
                                    onClick={() => setPointsForHalf((prev) => prev + 1)}
                                    type="button"
                                >

                                </button>
                                <button
                                    className="input-button decrease"
                                    onClick={() => setPointsForHalf((prev) => Math.max(prev - 1, 1))}
                                    type="button"
                                >

                                </button>
                            </div>
                            <input
                                type="number"
                                value={pointsForHalf}
                                onChange={(e) => setPointsForHalf(Number(e.target.value))}
                                className="points-input"
                                min="1"
                                required
                            />
                        </div>
                    </div>
                    <div className="form-group points-input-group">
                        <label>Points for full track:</label>
                        <div className="input-wrapper">
                            <div class="adjust-wrapper">
                                <button
                                    className="input-button increase"
                                    onClick={() => setPointsForFull((prev) => prev + 1)}
                                    type="button"
                                >

                                </button>
                                <button
                                    className="input-button decrease"
                                    onClick={() => setPointsForFull((prev) => Math.max(prev - 1, 1))}
                                    type="button"
                                >

                                </button>
                            </div>
                            <input
                                type="number"
                                value={pointsForFull}
                                onChange={(e) => setPointsForFull(Number(e.target.value))}
                                className="points-input"
                                min="1"
                                required
                            />

                        </div>
                    </div>
                    <button type="submit" className="submit-btn" disabled={loading}>
                        {loading ? "Creating..." : "Create Campaign"}
                    </button>
                </form>
                {message && <p className="message">{message}</p>}
            </Modal>
        </div>
    );
};

export default CreateCampaign;